import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Layout } from '../components/layout/Layout'
import { useAuth } from '../auth/AuthContext'

const API_BASE = import.meta.env.DEV ? '/api' : 'https://asia-northeast1-bluepaper2.cloudfunctions.net/api'

export type KnowledgeSubjectType = 'person' | 'project' | 'company'
export type KnowledgeKind =
  | 'fact_state' | 'fact_trait' | 'mindset' | 'framework'
  | 'business_model' | 'decision_case' | 'dont' | 'term' | 'relation'

export interface KnowledgeEntry {
  id: string
  subjectType: KnowledgeSubjectType
  subjectKey: string
  kind: KnowledgeKind
  text: string
  permanent: boolean
  confidential: boolean
  status: 'active' | 'archived'
  sources: { documentId: string; title: string; date: string }[]
  confidence: number
  referenceCount: number
  recognizedText?: string
  correctText?: string
}

interface KnowledgeCandidate extends KnowledgeEntry {
  candidateStatus: 'pending' | 'approved' | 'rejected'
  mergeTargetId?: string
  mergeSuggestion?: string
  createdBy: string
}

export const KIND_LABELS: Record<KnowledgeKind, string> = {
  fact_state: '事実（状態）',
  fact_trait: '事実（特性）',
  mindset: '考え方',
  framework: 'フレームワーク',
  business_model: 'ビジネスモデル',
  decision_case: '判断事例',
  dont: 'やらないこと',
  term: '用語',
  relation: '関係'
}

const SUBJECT_LABELS: Record<KnowledgeSubjectType, string> = {
  person: '人',
  project: '事業',
  company: '会社'
}

const KIND_FILTERS: { value: string; label: string }[] = [
  { value: 'fact', label: '事実' },
  { value: 'mindset', label: '考え方' },
  { value: 'framework', label: 'フレームワーク' },
  { value: 'business_model', label: 'ビジネスモデル' },
  { value: 'decision_case', label: '判断事例' },
  { value: 'dont', label: 'やらないこと' },
  { value: 'term', label: '用語' }
]

function KindBadge({ kind }: { kind: KnowledgeKind }) {
  return <span className="badge badge-neutral">{KIND_LABELS[kind] || kind}</span>
}

// ── 用語辞典（terminology コレクション）──
type TerminologyCategory = 'business' | 'technical' | 'names' | 'numbers'

interface TerminologyEntry {
  id: string
  recognizedText: string
  correctText: string
  category: TerminologyCategory
}

const TERM_CATEGORY_LABELS: Record<TerminologyCategory, string> = {
  business: 'ビジネス',
  technical: '技術',
  names: '名前・固有名詞',
  numbers: '数値'
}

export function ContextManagementPage() {
  const { user, firebaseUser } = useAuth()
  const [searchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const [activeTab, setActiveTab] = useState<'list' | 'inbox' | 'terms'>(
    tabParam === 'inbox' ? 'inbox' : tabParam === 'terms' ? 'terms' : 'list'
  )

  // 用語辞典
  const [terms, setTerms] = useState<TerminologyEntry[]>([])
  const [termsLoading, setTermsLoading] = useState(false)
  const [termForm, setTermForm] = useState({
    recognizedText: '',
    correctText: '',
    category: 'business' as TerminologyCategory
  })
  const [termAdding, setTermAdding] = useState(false)
  const [termDeletingId, setTermDeletingId] = useState<string | null>(null)
  const [termEditingId, setTermEditingId] = useState<string | null>(null)
  const [termEditForm, setTermEditForm] = useState<{ recognizedText: string; correctText: string; category: TerminologyCategory }>({ recognizedText: '', correctText: '', category: 'business' })
  const [termSaving, setTermSaving] = useState(false)

  const [entries, setEntries] = useState<KnowledgeEntry[]>([])
  const [candidates, setCandidates] = useState<KnowledgeCandidate[]>([])
  const [mergeTargets, setMergeTargets] = useState<Record<string, KnowledgeEntry>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  // フィルタ
  const [subjectFilter, setSubjectFilter] = useState<string | null>(searchParams.get('subjectType'))
  const [kindFilter, setKindFilter] = useState<string | null>(null)
  const [query, setQuery] = useState(searchParams.get('q') || '')
  const [showArchived, setShowArchived] = useState(false)

  // 手動追加フォーム
  const [showAddForm, setShowAddForm] = useState(false)
  const [addForm, setAddForm] = useState({
    subjectType: 'person' as KnowledgeSubjectType,
    subjectKey: '',
    kind: 'fact_state' as KnowledgeKind,
    text: '',
    confidential: false
  })
  const [adding, setAdding] = useState(false)

  // 候補の編集中テキスト
  const [editedTexts, setEditedTexts] = useState<Record<string, string>>({})
  const [processingId, setProcessingId] = useState<string | null>(null)

  // 編集中エントリ
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingText, setEditingText] = useState('')

  const [importing, setImporting] = useState(false)

  const authFetch = useCallback(async (path: string, init?: RequestInit) => {
    if (!firebaseUser) throw new Error('未認証です')
    const token = await firebaseUser.getIdToken()
    return fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(init?.headers || {})
      }
    })
  }, [firebaseUser])

  const fetchAll = useCallback(async () => {
    if (!firebaseUser) return
    setLoading(true)
    setError(null)
    try {
      const [kRes, cRes] = await Promise.all([
        authFetch('/v1/knowledge'),
        authFetch('/v1/knowledge/candidates')
      ])
      if (!kRes.ok) throw new Error(`ナレッジの取得に失敗しました (${kRes.status})`)
      if (!cRes.ok) throw new Error(`候補の取得に失敗しました (${cRes.status})`)
      const kData = await kRes.json()
      const cData = await cRes.json()
      setEntries(kData.knowledge || [])
      setCandidates(cData.candidates || [])
      setMergeTargets(cData.mergeTargets || {})
    } catch (e) {
      setError(e instanceof Error ? e.message : 'データの取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }, [firebaseUser, authFetch])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  const fetchTerms = useCallback(async () => {
    if (!firebaseUser || !user?.orgId) return
    setTermsLoading(true)
    setError(null)
    try {
      const res = await authFetch(`/v1/organizations/${user.orgId}/terminology`)
      if (!res.ok) throw new Error(`用語辞典の取得に失敗しました (${res.status})`)
      const data = await res.json()
      setTerms(data.terminology || [])
    } catch (e) {
      setError(e instanceof Error ? e.message : '用語辞典の取得に失敗しました')
    } finally {
      setTermsLoading(false)
    }
  }, [firebaseUser, user?.orgId, authFetch])

  useEffect(() => {
    if (activeTab === 'terms') fetchTerms()
  }, [activeTab, fetchTerms])

  const flash = (msg: string) => {
    setMessage(msg)
    setTimeout(() => setMessage(null), 3000)
  }

  const filtered = useMemo(() => {
    let list = entries.filter(e => (showArchived ? true : e.status === 'active'))
    if (subjectFilter) list = list.filter(e => e.subjectType === subjectFilter)
    if (kindFilter) {
      list = kindFilter === 'fact'
        ? list.filter(e => e.kind === 'fact_state' || e.kind === 'fact_trait')
        : list.filter(e => e.kind === kindFilter)
    }
    if (query.trim()) {
      const q = query.trim().toLowerCase()
      list = list.filter(e => e.subjectKey.toLowerCase().includes(q) || e.text.toLowerCase().includes(q))
    }
    return list
  }, [entries, subjectFilter, kindFilter, query, showArchived])

  const handleAdd = async () => {
    if (!addForm.subjectKey.trim() || !addForm.text.trim()) {
      setError('対象と内容を入力してください')
      return
    }
    setAdding(true)
    setError(null)
    try {
      const res = await authFetch('/v1/knowledge', {
        method: 'POST',
        body: JSON.stringify(addForm)
      })
      const data = await res.json().catch(() => null)
      if (!res.ok) throw new Error(data?.error || `追加に失敗しました (${res.status})`)
      setAddForm({ subjectType: 'person', subjectKey: '', kind: 'fact_state', text: '', confidential: false })
      setShowAddForm(false)
      flash('ナレッジを追加しました')
      await fetchAll()
    } catch (e) {
      setError(e instanceof Error ? e.message : '追加に失敗しました')
    } finally {
      setAdding(false)
    }
  }

  const patchEntry = async (id: string, body: Record<string, unknown>, successMsg: string) => {
    setError(null)
    try {
      const res = await authFetch(`/v1/knowledge/${id}`, { method: 'PATCH', body: JSON.stringify(body) })
      const data = await res.json().catch(() => null)
      if (!res.ok) throw new Error(data?.error || `更新に失敗しました (${res.status})`)
      flash(successMsg)
      await fetchAll()
    } catch (e) {
      setError(e instanceof Error ? e.message : '更新に失敗しました')
    }
  }

  const handleCandidateAction = async (cand: KnowledgeCandidate, action: 'approve' | 'reject') => {
    setProcessingId(cand.id)
    setError(null)
    try {
      const edited = editedTexts[cand.id]
      const res = await authFetch(`/v1/knowledge/candidates/${cand.id}/${action}`, {
        method: 'POST',
        body: JSON.stringify(action === 'approve' && edited !== undefined && edited !== cand.text ? { editedText: edited } : {})
      })
      const data = await res.json().catch(() => null)
      if (!res.ok) throw new Error(data?.error || `処理に失敗しました (${res.status})`)
      flash(action === 'approve' ? '候補を承認しました' : '候補を却下しました')
      await fetchAll()
    } catch (e) {
      setError(e instanceof Error ? e.message : '処理に失敗しました')
    } finally {
      setProcessingId(null)
    }
  }

  const handleAddTerm = async () => {
    if (!user?.orgId) return
    if (!termForm.recognizedText.trim() || !termForm.correctText.trim()) {
      setError('誤読と正しい表記を入力してください')
      return
    }
    setTermAdding(true)
    setError(null)
    try {
      const res = await authFetch(`/v1/organizations/${user.orgId}/terminology`, {
        method: 'POST',
        body: JSON.stringify({
          recognizedText: termForm.recognizedText.trim(),
          correctText: termForm.correctText.trim(),
          category: termForm.category
        })
      })
      const data = await res.json().catch(() => null)
      if (!res.ok) throw new Error(data?.error || `用語の追加に失敗しました (${res.status})`)
      setTermForm(f => ({ ...f, recognizedText: '', correctText: '' }))
      flash('用語を追加しました')
      await fetchTerms()
    } catch (e) {
      setError(e instanceof Error ? e.message : '用語の追加に失敗しました')
    } finally {
      setTermAdding(false)
    }
  }

  const handleSaveTermEdit = async (entry: TerminologyEntry) => {
    if (!user?.orgId) return
    if (!termEditForm.recognizedText.trim() || !termEditForm.correctText.trim()) return
    setTermSaving(true)
    setError(null)
    try {
      const res = await authFetch(`/v1/organizations/${user.orgId}/terminology/${entry.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recognizedText: termEditForm.recognizedText.trim(),
          correctText: termEditForm.correctText.trim(),
          category: termEditForm.category
        })
      })
      const data = await res.json().catch(() => null)
      if (!res.ok) throw new Error(data?.error || `用語の更新に失敗しました (${res.status})`)
      flash('用語を更新しました')
      setTermEditingId(null)
      await fetchTerms()
    } catch (e) {
      setError(e instanceof Error ? e.message : '用語の更新に失敗しました')
    } finally {
      setTermSaving(false)
    }
  }

  const handleDeleteTerm = async (entry: TerminologyEntry) => {
    if (!user?.orgId) return
    if (!window.confirm(`「${entry.recognizedText} → ${entry.correctText}」を削除しますか？`)) return
    setTermDeletingId(entry.id)
    setError(null)
    try {
      const res = await authFetch(`/v1/organizations/${user.orgId}/terminology/${entry.id}`, {
        method: 'DELETE'
      })
      const data = await res.json().catch(() => null)
      if (!res.ok) throw new Error(data?.error || `用語の削除に失敗しました (${res.status})`)
      flash('用語を削除しました')
      await fetchTerms()
    } catch (e) {
      setError(e instanceof Error ? e.message : '用語の削除に失敗しました')
    } finally {
      setTermDeletingId(null)
    }
  }

  /** 旧「コンテキスト情報管理」からの一括取り込み（組織情報 → 会社ナレッジ） */
  const handleImportLegacyContext = async () => {
    if (!user?.orgId) return
    setImporting(true)
    setError(null)
    try {
      const res = await authFetch(`/v1/organizations/${user.orgId}`)
      if (!res.ok) throw new Error(`組織情報の取得に失敗しました (${res.status})`)
      const org = await res.json()
      const orgName: string = org?.name || ''
      if (!orgName) throw new Error('取り込める組織情報が見つかりませんでした')
      const exists = entries.some(e => e.subjectType === 'company' && e.subjectKey === orgName)
      if (exists) {
        flash('組織情報はすでにナレッジに取り込まれています')
        return
      }
      const createRes = await authFetch('/v1/knowledge', {
        method: 'POST',
        body: JSON.stringify({
          subjectType: 'company',
          subjectKey: orgName,
          kind: 'fact_trait',
          text: `自組織。組織名は「${orgName}」。`,
          permanent: true
        })
      })
      if (!createRes.ok) {
        const data = await createRes.json().catch(() => null)
        throw new Error(data?.error || '取り込みに失敗しました')
      }
      flash('既存のコンテキスト情報をナレッジに取り込みました')
      await fetchAll()
    } catch (e) {
      setError(e instanceof Error ? e.message : '取り込みに失敗しました')
    } finally {
      setImporting(false)
    }
  }

  return (
    <Layout title="コンテキスト">
      <p className="form-section-desc">
        議事録から抽出された組織ナレッジ（人・事業・会社に関する事実、考え方、用語など）を管理します。
        承認されたナレッジは、以降の議事録AI整形時のコンテキストとして自動的に活用されます。
      </p>

      <div className="tab-list">
        <button className={`tab-item ${activeTab === 'list' ? 'active' : ''}`} onClick={() => setActiveTab('list')}>
          ナレッジ一覧
        </button>
        <button className={`tab-item ${activeTab === 'inbox' ? 'active' : ''}`} onClick={() => setActiveTab('inbox')}>
          レビュー受信箱{candidates.length > 0 ? `（${candidates.length}）` : ''}
        </button>
        <button className={`tab-item ${activeTab === 'terms' ? 'active' : ''}`} onClick={() => setActiveTab('terms')}>
          用語辞典
        </button>
      </div>

      {error && <div className="alert danger">{error}</div>}
      {message && <div className="alert success">{message}</div>}

      {activeTab === 'terms' ? (
        <div className="card">
          <div className="card-header">
            <div>
              <h2 className="text-lg font-semibold">用語辞典</h2>
              <p className="form-section-desc">
                音声認識で誤読されやすい専門用語・固有名詞の正しい表記を登録します。AI整形時に自動で置換・参照されます。
              </p>
            </div>
          </div>

          {/* 追加フォーム */}
          <div className="form-section" style={{ marginBottom: 16 }}>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="text"
                className="form-input"
                style={{ flex: '1 1 160px' }}
                placeholder="誤読（音声認識結果）"
                value={termForm.recognizedText}
                onChange={(e) => setTermForm(f => ({ ...f, recognizedText: e.target.value }))}
              />
              <span className="text-tertiary">→</span>
              <input
                type="text"
                className="form-input"
                style={{ flex: '1 1 160px' }}
                placeholder="正しい表記"
                value={termForm.correctText}
                onChange={(e) => setTermForm(f => ({ ...f, correctText: e.target.value }))}
              />
              <select
                className="form-input"
                style={{ width: 'auto' }}
                value={termForm.category}
                onChange={(e) => setTermForm(f => ({ ...f, category: e.target.value as TerminologyCategory }))}
              >
                {(Object.keys(TERM_CATEGORY_LABELS) as TerminologyCategory[]).map(c => (
                  <option key={c} value={c}>{TERM_CATEGORY_LABELS[c]}</option>
                ))}
              </select>
              <button
                className="btn btn-primary btn-sm"
                disabled={termAdding || !termForm.recognizedText.trim() || !termForm.correctText.trim()}
                onClick={handleAddTerm}
              >
                {termAdding && <span className="spinner sm" />}
                追加
              </button>
            </div>
          </div>

          {/* 一覧 */}
          {termsLoading ? (
            <div className="flex items-center gap-2 py-4">
              <span className="spinner" />
              <span className="text-secondary text-sm">読み込み中...</span>
            </div>
          ) : terms.length === 0 ? (
            <p className="text-sm text-secondary py-4">登録された用語はまだありません。</p>
          ) : (
            <div className="flex flex-col gap-1">
              {terms.map(entry => (
                <div
                  key={entry.id}
                  className="flex items-center justify-between px-3 py-2 rounded-md gap-2"
                  style={{ background: 'var(--color-surface)' }}
                >
                  {termEditingId === entry.id ? (
                    <>
                      <span className="text-sm flex items-center gap-2 flex-wrap" style={{ flex: 1, minWidth: 0 }}>
                        <input
                          type="text"
                          className="form-input text-sm"
                          style={{ flex: '1 1 120px', padding: '4px 8px' }}
                          value={termEditForm.recognizedText}
                          onChange={(e) => setTermEditForm(f => ({ ...f, recognizedText: e.target.value }))}
                        />
                        <span className="text-tertiary">→</span>
                        <input
                          type="text"
                          className="form-input text-sm"
                          style={{ flex: '1 1 120px', padding: '4px 8px' }}
                          value={termEditForm.correctText}
                          onChange={(e) => setTermEditForm(f => ({ ...f, correctText: e.target.value }))}
                        />
                        <select
                          className="form-input text-sm"
                          style={{ width: 'auto', padding: '4px 8px' }}
                          value={termEditForm.category}
                          onChange={(e) => setTermEditForm(f => ({ ...f, category: e.target.value as TerminologyCategory }))}
                        >
                          {(Object.keys(TERM_CATEGORY_LABELS) as TerminologyCategory[]).map(c => (
                            <option key={c} value={c}>{TERM_CATEGORY_LABELS[c]}</option>
                          ))}
                        </select>
                      </span>
                      <span className="flex items-center gap-1" style={{ flexShrink: 0 }}>
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={termSaving || !termEditForm.recognizedText.trim() || !termEditForm.correctText.trim()}
                          onClick={() => handleSaveTermEdit(entry)}
                        >
                          {termSaving && <span className="spinner sm" />}
                          保存
                        </button>
                        <button className="btn btn-outline btn-sm" disabled={termSaving} onClick={() => setTermEditingId(null)}>
                          キャンセル
                        </button>
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="text-sm flex items-center gap-2 flex-wrap">
                        <span className="text-secondary">{entry.recognizedText}</span>
                        <span className="text-tertiary">→</span>
                        <span className="font-medium">{entry.correctText}</span>
                        <span className="badge badge-neutral">{TERM_CATEGORY_LABELS[entry.category] || entry.category}</span>
                      </span>
                      <span className="flex items-center gap-1" style={{ flexShrink: 0 }}>
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => {
                            setTermEditingId(entry.id)
                            setTermEditForm({ recognizedText: entry.recognizedText, correctText: entry.correctText, category: entry.category })
                          }}
                        >
                          編集
                        </button>
                        <button
                          className="btn btn-outline btn-sm"
                          disabled={termDeletingId === entry.id}
                          onClick={() => handleDeleteTerm(entry)}
                          style={{ color: 'var(--color-danger)' }}
                        >
                          {termDeletingId === entry.id && <span className="spinner sm" />}
                          削除
                        </button>
                      </span>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : loading ? (
        <div className="flex items-center gap-2 py-8 justify-center">
          <span className="spinner" />
          <span className="text-secondary text-sm">読み込み中...</span>
        </div>
      ) : activeTab === 'list' ? (
        <div className="card">
          <div className="card-header">
            <div>
              <h2 className="text-lg font-semibold">ナレッジ一覧</h2>
              <p className="form-section-desc">{filtered.length}件のナレッジ</p>
            </div>
            <div className="flex items-center gap-2">
              <button className="btn btn-outline btn-sm" disabled={importing} onClick={handleImportLegacyContext}>
                {importing && <span className="spinner sm" />}
                既存のコンテキスト情報をナレッジに取り込む
              </button>
              <button className="btn btn-primary btn-sm" onClick={() => setShowAddForm(v => !v)}>
                {showAddForm ? '閉じる' : '手動で追加'}
              </button>
            </div>
          </div>

          {showAddForm && (
            <div className="form-section" style={{ marginBottom: 16 }}>
              <h3 className="form-section-title">ナレッジを追加</h3>
              <div className="field-grid">
                <div className="form-group">
                  <label className="form-label">対象タイプ</label>
                  <select
                    className="form-input"
                    value={addForm.subjectType}
                    onChange={(e) => setAddForm(f => ({ ...f, subjectType: e.target.value as KnowledgeSubjectType }))}
                  >
                    <option value="person">人</option>
                    <option value="project">事業</option>
                    <option value="company">会社</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">対象（人名 / プロジェクト名 / 会社名）</label>
                  <input
                    type="text"
                    className="form-input"
                    value={addForm.subjectKey}
                    onChange={(e) => setAddForm(f => ({ ...f, subjectKey: e.target.value }))}
                    placeholder="山田太郎"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">種類</label>
                  <select
                    className="form-input"
                    value={addForm.kind}
                    onChange={(e) => setAddForm(f => ({ ...f, kind: e.target.value as KnowledgeKind }))}
                  >
                    {(Object.keys(KIND_LABELS) as KnowledgeKind[]).map(k => (
                      <option key={k} value={k}>{KIND_LABELS[k]}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">内容（1〜3行に圧縮）</label>
                <textarea
                  rows={2}
                  className="form-input"
                  value={addForm.text}
                  onChange={(e) => setAddForm(f => ({ ...f, text: e.target.value }))}
                  placeholder="例: 意思決定はジョブサイズで優先度を判断する"
                />
              </div>
              <div className="flex items-center justify-between">
                <label className="toggle-wrapper">
                  <span className="toggle">
                    <input
                      type="checkbox"
                      checked={addForm.confidential}
                      onChange={(e) => setAddForm(f => ({ ...f, confidential: e.target.checked }))}
                    />
                    <span className="toggle-track"></span>
                    <span className="toggle-thumb"></span>
                  </span>
                  <span className="toggle-label">機密情報</span>
                </label>
                <button className="btn btn-primary btn-sm" disabled={adding} onClick={handleAdd}>
                  {adding && <span className="spinner sm" />}
                  追加
                </button>
              </div>
            </div>
          )}

          {/* フィルタ */}
          <div className="flex flex-col gap-2" style={{ marginBottom: 16 }}>
            <input
              type="text"
              className="form-input"
              placeholder="ナレッジを検索..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-xs text-secondary">対象:</span>
              {(['person', 'project', 'company'] as const).map(st => (
                <button
                  key={st}
                  className={`chip${subjectFilter === st ? ' active' : ''}`}
                  onClick={() => setSubjectFilter(subjectFilter === st ? null : st)}
                >
                  {SUBJECT_LABELS[st]}
                </button>
              ))}
              <span className="text-xs text-secondary" style={{ marginLeft: 8 }}>種類:</span>
              {KIND_FILTERS.map(kf => (
                <button
                  key={kf.value}
                  className={`chip${kindFilter === kf.value ? ' active' : ''}`}
                  onClick={() => setKindFilter(kindFilter === kf.value ? null : kf.value)}
                >
                  {kf.label}
                </button>
              ))}
              <button
                className={`chip${showArchived ? ' active' : ''}`}
                style={{ marginLeft: 'auto' }}
                onClick={() => setShowArchived(v => !v)}
              >
                アーカイブ済みも表示
              </button>
            </div>
          </div>

          {/* 一覧 */}
          {filtered.length === 0 ? (
            <p className="text-sm text-secondary py-4">
              ナレッジはまだありません。議事録のAI整形が完了すると候補が自動抽出され、レビュー受信箱に届きます。
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {filtered.map(entry => (
                <div
                  key={entry.id}
                  className="flex items-start gap-3 px-3 py-2 rounded-md"
                  style={{ background: 'var(--color-surface)', opacity: entry.status === 'archived' ? 0.6 : 1 }}
                >
                  <div className="flex flex-col gap-1 min-w-0" style={{ flex: 1 }}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="chip">{SUBJECT_LABELS[entry.subjectType]}・{entry.subjectKey}</span>
                      <KindBadge kind={entry.kind} />
                      {entry.permanent && <span title="恒久ナレッジ">🔒</span>}
                      {entry.confidential && <span title="機密">🤫</span>}
                      {entry.status === 'archived' && <span className="badge badge-neutral">アーカイブ</span>}
                    </div>
                    {editingId === entry.id ? (
                      <div className="flex flex-col gap-2">
                        <textarea
                          rows={2}
                          className="form-input"
                          value={editingText}
                          onChange={(e) => setEditingText(e.target.value)}
                        />
                        <div className="flex gap-2">
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={async () => {
                              await patchEntry(entry.id, { text: editingText }, 'ナレッジを更新しました')
                              setEditingId(null)
                            }}
                          >
                            保存
                          </button>
                          <button className="btn btn-outline btn-sm" onClick={() => setEditingId(null)}>キャンセル</button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm" style={{ whiteSpace: 'pre-wrap' }}>{entry.text}</p>
                    )}
                    <span className="text-xs text-tertiary">
                      確度 {entry.confidence} ・ 参照 {entry.referenceCount}回
                      {entry.sources.length > 0 && (
                        <>
                          {' ・ 出典: '}
                          <Link to={`/documents/${entry.sources[entry.sources.length - 1].documentId}`}>
                            {entry.sources[entry.sources.length - 1].title}
                          </Link>
                        </>
                      )}
                    </span>
                  </div>
                  {editingId !== entry.id && (
                    <div className="flex gap-1" style={{ flexShrink: 0 }}>
                      <button
                        className="btn btn-outline btn-sm"
                        onClick={() => { setEditingId(entry.id); setEditingText(entry.text) }}
                      >
                        編集
                      </button>
                      <button
                        className="btn btn-outline btn-sm"
                        title={entry.permanent ? '恒久指定を解除（月次レビュー対象になります）' : '恒久ナレッジに指定（自動アーカイブ対象外）'}
                        onClick={() => patchEntry(entry.id, { permanent: !entry.permanent }, entry.permanent ? '恒久指定を解除しました' : '恒久ナレッジに指定しました')}
                      >
                        {entry.permanent ? '恒久解除' : '恒久化'}
                      </button>
                      <button
                        className="btn btn-outline btn-sm"
                        onClick={() => patchEntry(
                          entry.id,
                          { status: entry.status === 'archived' ? 'active' : 'archived' },
                          entry.status === 'archived' ? '復元しました' : 'アーカイブしました'
                        )}
                      >
                        {entry.status === 'archived' ? '復元' : 'アーカイブ'}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {candidates.length === 0 ? (
            <div className="card">
              <p className="text-sm text-secondary py-4 text-center">
                レビュー待ちの候補はありません。議事録のAI整形が完了すると、新しいナレッジ候補がここに届きます。
              </p>
            </div>
          ) : (
            candidates.map(cand => {
              const isArchiveProposal = cand.createdBy === 'system:monthly-review'
              const mergeTarget = cand.mergeTargetId ? mergeTargets[cand.mergeTargetId] : undefined
              const processing = processingId === cand.id
              return (
                <div key={cand.id} className="card">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="chip">{SUBJECT_LABELS[cand.subjectType]}・{cand.subjectKey}</span>
                    <KindBadge kind={cand.kind} />
                    {cand.confidential && <span title="機密">🤫</span>}
                    {isArchiveProposal && <span className="badge badge-neutral">月次レビュー</span>}
                  </div>

                  {cand.mergeSuggestion && (
                    <div className="alert info" style={{ marginBottom: 8 }}>{cand.mergeSuggestion}</div>
                  )}

                  {isArchiveProposal ? (
                    <p className="text-sm" style={{ whiteSpace: 'pre-wrap' }}>{cand.text}</p>
                  ) : (
                    <>
                      {mergeTarget && !isArchiveProposal && (
                        <div className="form-group">
                          <label className="form-label">既存のナレッジ</label>
                          <div
                            className="text-sm px-3 py-2 rounded-md"
                            style={{ background: 'var(--color-surface)', textDecoration: 'line-through', opacity: 0.75 }}
                          >
                            {mergeTarget.text}
                          </div>
                        </div>
                      )}
                      <div className="form-group">
                        <label className="form-label">{mergeTarget ? '新しい内容（編集可）' : '提案内容（編集可）'}</label>
                        <textarea
                          rows={2}
                          className="form-input"
                          value={editedTexts[cand.id] ?? cand.text}
                          onChange={(e) => setEditedTexts(t => ({ ...t, [cand.id]: e.target.value }))}
                        />
                      </div>
                    </>
                  )}

                  {cand.sources.length > 0 && (
                    <p className="text-xs text-tertiary mb-2">
                      出典: <Link to={`/documents/${cand.sources[0].documentId}`}>{cand.sources[0].title}</Link>
                      {cand.sources[0].date ? `（${cand.sources[0].date}）` : ''}
                    </p>
                  )}

                  <div className="flex gap-2 justify-end">
                    {isArchiveProposal ? (
                      <>
                        <button
                          className="btn btn-outline btn-sm"
                          disabled={processing}
                          onClick={() => handleCandidateAction(cand, 'reject')}
                        >
                          残す
                        </button>
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={processing}
                          onClick={() => handleCandidateAction(cand, 'approve')}
                        >
                          {processing && <span className="spinner sm" />}
                          アーカイブする
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="btn btn-outline btn-sm"
                          disabled={processing}
                          onClick={() => handleCandidateAction(cand, 'reject')}
                        >
                          却下
                        </button>
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={processing}
                          onClick={() => handleCandidateAction(cand, 'approve')}
                        >
                          {processing && <span className="spinner sm" />}
                          {editedTexts[cand.id] !== undefined && editedTexts[cand.id] !== cand.text ? '編集して承認' : '承認'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )
            })
          )}
        </div>
      )}
    </Layout>
  )
}
